import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * "Switch to the 3-month plan" on a monthly Booster/Luxe cart line (snippets/cart-products.liquid).
 * Adds the 3-month plan product with its selling plan first (so the cart is never empty), then removes the
 * monthly line and re-renders every cart section from that response, the same way product-form.js does.
 */
class RblPlanUpgrade extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#upgrade);
  }

  #upgrade = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: this.dataset.lineKey, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
      const added = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: json,
        body: JSON.stringify({
          items: [{ id: Number(this.dataset.variant), quantity: 1, selling_plan: Number(this.dataset.plan) }],
        }),
      }).then((r) => r.json());
      if (added.status) throw new Error(added.description || added.message || 'Could not add the 3-month plan');

      const changed = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: json,
        body: JSON.stringify({
          id: this.dataset.lineKey,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(changed),
        detail: {
          items: changed.items,
          itemCount: changed.item_count,
          sections: changed.sections ?? undefined,
          source: 'rbl-plan-upgrade',
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('rbl-plan-upgrade')) customElements.define('rbl-plan-upgrade', RblPlanUpgrade);
